/**
 * Order total row for checkout price summaries (#1416).
 * Shows a loader until every quoted dependency and server total are final.
 */

import { Group, Loader, Text } from '@mantine/core';
import { semanticColors } from '../../config/theme';
import { PRICE_SUMMARY_TOTAL_LINE_TEST_ID } from '../../config/testIds';
import { OrderTaxLineLabels } from '../../constants/orderTaxLineLabels';
import { formatUsd } from '../../utils/formatUsd';

export interface PriceSummaryTotalLineProps {
  loading?: boolean;
  /** When true, show deferred copy instead of loader or dollars (guest / tax unavailable). */
  deferred?: boolean;
  totalUsd?: number | null;
  /** Defaults to Total. */
  label?: string;
  /** Override deferred column copy; defaults to {@link OrderTaxLineLabels.totalCalculatedAtReview}. */
  deferredLabel?: string;
}

/**
 * Renders the bottom total line with loader, deferred copy, or authoritative amount.
 */
export function PriceSummaryTotalLine({
  loading = false,
  deferred = false,
  totalUsd = null,
  label = 'Total',
  deferredLabel = OrderTaxLineLabels.totalCalculatedAtReview,
}: PriceSummaryTotalLineProps) {
  return (
    <Group justify="space-between" wrap="nowrap" data-testid={PRICE_SUMMARY_TOTAL_LINE_TEST_ID}>
      <Text size="sm" fw={600}>
        {label}
      </Text>
      {deferred ? (
        <Text size="sm" c={semanticColors.text.secondary} data-testid="price-summary-total-deferred">
          {deferredLabel}
        </Text>
      ) : loading || totalUsd == null ? (
        <Loader size="xs" aria-label="Calculating order total" />
      ) : (
        <Text size="sm" fw={600} aria-label={formatUsd(totalUsd)}>
          {formatUsd(totalUsd)}
        </Text>
      )}
    </Group>
  );
}
